import React from 'react';
import { motion } from 'framer-motion';
import Icon from 'components/AppIcon';

// ─── Error Card Config ─────────────────────────────────────────────────────────
const ERROR_CONFIG = {
  unsupported: {
    icon: 'AlertTriangle',
    iconColor: '#EF4444',
    borderColor: 'rgba(239,68,68,0.35)',
    badgeColor: 'rgba(239,68,68,0.12)',
    title: 'Browser Not Supported',
    description:
      "Your current browser doesn't support camera access required for AR scanning.",
    steps: [
      'Switch to Chrome 80+, Firefox 75+, Safari 14+, or Edge 80+',
      "Make sure you're not using an in-app browser (e.g. Instagram, Facebook browser)",
      "On iOS, use Safari — other browsers on iOS don't support camera access",
      'Ensure your browser is up to date',
    ],
    primaryAction: {
      label: 'Download Chrome',
      href: 'https://www.google.com/chrome',
      variant: 'outline',
    },
  },
  not_found: {
    icon: 'Camera',
    iconColor: '#F59E0B',
    borderColor: 'rgba(245,158,11,0.35)',
    badgeColor: 'rgba(245,158,11,0.12)',
    title: 'No Camera Detected',
    description: "FinVisAR couldn\'t find a camera on your device.",
    steps: [
      'Make sure your device has a working camera',
      'Check if an external camera is properly connected',
      'On desktop: go to Device Manager (Windows) or System Preferences (Mac) and verify camera is recognised',
      'Close other apps that may be using the camera (Zoom, Teams, etc.)',
      'Try refreshing the page or restarting your browser',
    ],
    primaryAction: {
      label: 'Try Again',
      variant: 'filled',
    },
    secondaryAction: {
      label: 'Use Manual Product Entry instead',
      action: 'manual',
    },
  },
  timeout: {
    icon: 'Clock',
    iconColor: '#F97316',
    borderColor: 'rgba(249,115,22,0.35)',
    badgeColor: 'rgba(249,115,22,0.12)',
    title: 'Camera Stream Timed Out',
    description:
      'The camera took too long to respond. This can happen due to hardware or permission delays.',
    steps: [
      "Click \'Allow\' when your browser asks for camera permission",
      "Check if camera permissions are blocked — click the lock icon in your browser's address bar",
      'On Chrome: go to Settings → Privacy & Security → Site Settings → Camera and allow this site',
      'On Safari: go to Settings → Websites → Camera and set to Allow',
      'Try disconnecting and reconnecting external cameras',
      'Restart your browser and try again',
    ],
    primaryAction: {
      label: 'Retry Camera',
      variant: 'filled',
    },
  },
};

// ─── Component ─────────────────────────────────────────────────────────────────
export default function ARScannerErrorCard({
  errorState,
  onRetry,
  onManualEntry,
  isRetrying,
}) {
  const { type, retryCount } = errorState || {};
  if (!type) return null;

  const config = ERROR_CONFIG?.[type];
  if (!config) return null;

  const maxRetries = 3;
  const exhausted = retryCount >= maxRetries;

  return (
    <motion.div
      key={type}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="absolute inset-0 flex items-center justify-center p-4 md:p-6 overflow-y-auto"
      style={{ background: 'rgba(2,6,23,0.97)' }}
    >
      <div
        className="w-full max-w-sm rounded-xl p-5 md:p-6 flex flex-col gap-4"
        style={{
          background: '#1a2a3a',
          border: `1.5px solid ${config?.borderColor}`,
          borderRadius: '12px',
        }}
      >
        {/* Icon badge */}
        <div className="flex items-center gap-3">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 relative"
            style={{ background: config?.badgeColor, border: `1px solid ${config?.borderColor}` }}
          >
            <Icon name={config?.icon} size={22} color={config?.iconColor} strokeWidth={2} />
            {/* Camera X overlay for not_found */}
            {type === 'not_found' && (
              <div
                className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center"
                style={{ background: '#1a2a3a', border: '1px solid rgba(245,158,11,0.4)' }}
              >
                <Icon name="X" size={9} color="#F59E0B" strokeWidth={3} />
              </div>
            )}
          </div>
          <div>
            <h3 className="font-heading font-semibold text-slate-100 text-base leading-tight">
              {config?.title}
            </h3>
          </div>
        </div>

        {/* Description */}
        <p className="text-slate-400 text-sm leading-relaxed">{config?.description}</p>

        {/* Exhausted retries message (timeout only) */}
        {type === 'timeout' && exhausted && (
          <div
            className="rounded-lg px-4 py-3 text-sm"
            style={{ background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.2)' }}
          >
            <p className="text-orange-300 font-medium mb-1">Still having trouble?</p>
            <p className="text-slate-400 text-xs">
              Camera may be in use by another app.{' '}
              <a
                href="mailto:support@finvisar.com"
                className="text-orange-400 underline hover:text-orange-300 transition-colors"
              >
                Report Issue
              </a>
            </p>
          </div>
        )}

        {/* Troubleshooting steps */}
        <div>
          <p
            className="text-xs font-semibold uppercase tracking-wider mb-2"
            style={{ color: config?.iconColor, opacity: 0.8 }}
          >
            Troubleshooting Steps
          </p>
          <ul className="space-y-2">
            {config?.steps?.map((step, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span
                  className="flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold mt-0.5"
                  style={{
                    background: config?.badgeColor,
                    color: config?.iconColor,
                    border: `1px solid ${config?.borderColor}`,
                  }}
                >
                  {i + 1}
                </span>
                <span className="text-slate-400 text-xs leading-relaxed">{step}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Retry progress indicator */}
        {type === 'timeout' && isRetrying && (
          <div
            className="rounded-lg px-4 py-3 flex items-center gap-3"
            style={{ background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.2)' }}
          >
            <div
              className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin flex-shrink-0"
              style={{ borderColor: `${config?.iconColor} transparent transparent transparent` }}
            />
            <span className="text-orange-300 text-xs">
              Attempting to connect... (attempt {retryCount} of {maxRetries})
            </span>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-2.5 pt-1">
          {/* Primary action */}
          {!(type === 'timeout' && exhausted) && (
            <>
              {config?.primaryAction?.href ? (
                <a
                  href={config?.primaryAction?.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-colors min-h-[44px]"
                  style={{
                    border: `1.5px solid #00E5FF`,
                    color: '#00E5FF',
                    background: 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(0,229,255,0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <Icon name="ExternalLink" size={15} color="#00E5FF" />
                  {config?.primaryAction?.label}
                </a>
              ) : (
                <button
                  onClick={onRetry}
                  disabled={isRetrying}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-colors min-h-[44px] disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{
                    background:
                      config?.primaryAction?.variant === 'filled' ?'#00E5FF' :'transparent',
                    color:
                      config?.primaryAction?.variant === 'filled' ? '#0D1B2A' : '#00E5FF',
                    border:
                      config?.primaryAction?.variant === 'outline' ?'1.5px solid #00E5FF' :'none',
                  }}
                >
                  {isRetrying ? (
                    <div
                      className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin"
                      style={{ borderColor: '#0D1B2A transparent transparent transparent' }}
                    />
                  ) : (
                    <Icon
                      name="RefreshCw"
                      size={15}
                      color={config?.primaryAction?.variant === 'filled' ? '#0D1B2A' : '#00E5FF'}
                    />
                  )}
                  {config?.primaryAction?.label}
                </button>
              )}
            </>
          )}

          {/* Secondary action */}
          {config?.secondaryAction && (
            <button
              onClick={onManualEntry}
              className="w-full text-center text-xs text-slate-400 hover:text-cyan-400 transition-colors py-1 underline underline-offset-2"
            >
              {config?.secondaryAction?.label}
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}
